import type { PurchaseInfo } from '@setplex/tria-api'
import type { Store } from 'effector'

import { combine, createEvent, sample } from 'effector'
import { model as router } from '~/core/router'
import { siginInToWatchFeatureModel } from '~/features/signin-to-watch'
import { model as guestModel } from '~/features/subscriber/guest'
import { aye } from '~/shared/helpers'

interface SignInToWatchEntity {
  id?: number
  name?: string
  free?: boolean
  purchaseInfo?: PurchaseInfo
}

export const genSignInToWatchLogic = ({
  entityStore,
  redirectPath,
}: {
  entityStore: Store<SignInToWatchEntity | null>
  redirectPath: Store<string | null>
}) => {
  const $shouldShowSignInToWatchPopup = combine(
    {
      entity: entityStore,
      guest: guestModel.$guest,
      query: router.$query,
    },
    ({ guest, entity, query }) => {
      // Trailer never needs sign-in — resolve before entity so PlayerOrderedViews can become ready
      if (query.has('trailer')) return false
      if (!entity) return null

      return aye(guest) && !entity.free && !entity.purchaseInfo
    }
  )

  const signInToWatchPopupSubmit = createEvent()

  sample({
    clock: signInToWatchPopupSubmit,
    source: {
      entity: entityStore,
      redirect: redirectPath,
      shouldShow: $shouldShowSignInToWatchPopup,
    },
    filter: ({ entity, shouldShow }) => aye(entity) && aye(shouldShow),
    fn: ({ redirect, entity }) => {
      const { name } = entity!

      return {
        name: name ?? '',
        redirect: redirect ?? '',
      }
    },
    target: [
      siginInToWatchFeatureModel.$signInToWatchItem,
      siginInToWatchFeatureModel.signInToWatch,
    ],
  })

  return {
    $showSignInToWatchPopup: $shouldShowSignInToWatchPopup,
    signInToWatchPopupSubmit,
    resetSignInToWatchItem: siginInToWatchFeatureModel.resetSignInToWatchItem,
  }
}
